import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loading from '../../component/Loading';
import { FaArrowLeft, FaDownload, FaCalendarAlt, FaUser, FaBuilding, FaEnvelope, FaCheckCircle, FaTimesCircle, FaClock, FaFileAlt, FaImage, FaTimes, FaPrint, FaFileExcel, FaRegFileExcel, FaSignInAlt, FaSignOutAlt, FaCalendarTimes, FaNetworkWired, FaMapMarkerAlt } from 'react-icons/fa';
import { formatDate, formatTime, formatJamKerja, formatDate01, formatNumber, formatDateOnly, formatDayName } from '../../helper/helper';
import AuthImage from '../../component/common/AuthImage';
import swal from 'sweetalert';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../../component/Panel/CalendarComponent.css';
import Modal from 'react-modal';

const ReportUser = ({ data }) => {
    const { id_user } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [selectedDate, setSelectedDate] = useState(null);
    const [detailModal, setDetailModal] = useState({ isOpen: false, data: null });
    const [imageModal, setImageModal] = useState({ isOpen: false, imageUrl: null, title: '', attendanceData: null, type: null });
    const [hariLibur, setHariLibur] = useState([]);
    const tableRef = useRef(null);

    useEffect(() => {
        getReportData();
        getHariLibur();
        // eslint-disable-next-line
    }, [id_user, selectedMonth]);

    const getHariLibur = async () => {
        try {
            const [year, month] = selectedMonth.split('-').map(Number);
            const response = await axios.get(`/api/hari-libur/bulan/${year}/${month}`, {
                withCredentials: true
            });
            setHariLibur(response.data || []);
        } catch (error) {
            console.error("Error fetching hari libur:", error);
            setHariLibur([]);
        }
    };

    const getReportData = async () => {
        if (!id_user || isNaN(Number(id_user))) {
            swal("Error", "ID user tidak valid", "error");
            navigate('/admin/users');
            return;
        }

        setLoading(true);
        try {
            const monthParam = selectedMonth ? `?month=${selectedMonth}-01` : '';
            const response = await axios.get(`api/absensi/report/${id_user}${monthParam}`, {
                withCredentials: true
            });
            setReportData(response.data);
        } catch (error) {
            console.error('Error fetching report data:', error);
            const errorMessage = error.response?.data?.message || "Gagal memuat data report";
            swal("Error", errorMessage, "error");
            if (error.response?.status === 400) {
                // If validation error, redirect back to users page
                setTimeout(() => navigate('/admin/users'), 2000);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        if (!id_user || isNaN(Number(id_user))) {
            swal("Error", "ID user tidak valid", "error");
            return;
        }

        try {
            const monthParam = selectedMonth ? `?month=${selectedMonth}-01` : '';
            const response = await axios.get(`api/absensi/export/${id_user}${monthParam}`, {
                withCredentials: true,
                responseType: 'blob'
            });

            // Create blob and download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Rekap_Absensi_${selectedMonth}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            swal("Success", "File Excel berhasil diunduh", "success");
        } catch (error) {
            console.error('Error exporting Excel:', error);
            swal("Error", "Gagal mengunduh file Excel", "error");
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDateClick = (date) => {
        setSelectedDate(date);
        // Scroll to table and highlight the row
        if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const closeDetailModal = () => {
        setDetailModal({ isOpen: false, data: null });
    };

    const openImageModal = (imageUrl, title, attendanceData = null, type = null) => {
        if (imageUrl) {
            setImageModal({ isOpen: true, imageUrl: imageUrl, title, attendanceData, type });
        }
    };

    const closeImageModal = () => {
        setImageModal({ isOpen: false, imageUrl: null, title: '', attendanceData: null, type: null });
    };

    // Create a memoized map of dates to attendance items for efficient lookup
    // Use formatDate01 like DetailUser.jsx for consistency
    const attendanceDateMap = useMemo(() => {
        if (!reportData || !reportData.attendanceData) return new Map();

        const dateMap = new Map();
        reportData.attendanceData.forEach(item => {
            if (!item.tanggal) return;

            // Use formatDate01 to normalize tanggal (same as DetailUser.jsx)
            const dateStr = formatDate01(item.tanggal);

            if (dateStr) {
                // Store the item in map with normalized date as key
                dateMap.set(dateStr, item);
            }
        });

        return dateMap;
    }, [reportData]);

    // Generate all days in the month with attendance data (like Excel export)
    const allDaysInMonth = useMemo(() => {
        if (!selectedMonth) return [];

        const [year, month] = selectedMonth.split('-').map(Number);
        const daysInMonth = new Date(year, month, 0).getDate();
        const days = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month - 1, day);
            const dateStr = formatDate01(date);
            const attendanceItem = attendanceDateMap.get(dateStr);
            const dayOfWeek = date.getDay();
            const isHoliday = hariLibur.some(hl => formatDate01(hl.tanggal) === dateStr);
            const holiday = hariLibur.find(hl => formatDate01(hl.tanggal) === dateStr);

            // Create day object
            let dayData = {
                tanggal: date,
                dateStr: dateStr,
                dayOfWeek: dayOfWeek,
                isHoliday: isHoliday,
                holiday: holiday,
                attendance: attendanceItem || null
            };

            days.push(dayData);
        }

        return days;
    }, [selectedMonth, attendanceDateMap, hariLibur]);

    const tileContent = ({ date, view }) => {
        if (view === 'month' && reportData && reportData.attendanceData) {
            // Use formatDate01 to normalize calendar date (same as DetailUser.jsx)
            const dateStr = formatDate01(date);

            // Look up attendance item from memoized map
            const attendanceItem = attendanceDateMap.get(dateStr);
            const isHoliday = hariLibur.some(hl => formatDate01(hl.tanggal) === dateStr);

            // Show attendance marker if there's attendance data (priority)
            if (attendanceItem) {
                // Different colors based on status - EXACT same logic as getStatusBadge
                // Priority 1: Check status first (izin/cuti) - must be exact match
                const status = attendanceItem.status ? String(attendanceItem.status).toLowerCase().trim() : null;

                let bgColor = 'bg-red-500'; // Default: Tidak Hadir
                let text = null;

                if (status === 'izin') {
                    bgColor = 'bg-blue-500';
                    text = 'izin';
                } else if (status === 'cuti') {
                    bgColor = 'bg-purple-500';
                    text = 'cuti';
                }
                // Priority 2: Check attendance (only if no status or status is not izin/cuti)
                else if (attendanceItem.waktuMasuk && attendanceItem.waktuKeluar) {
                    bgColor = 'bg-green-500'; // Hadir lengkap
                } else if (attendanceItem.waktuMasuk) {
                    bgColor = 'bg-yellow-500'; // Hanya masuk
                } else {
                    bgColor = 'bg-red-500'; // Tidak hadir
                }

                if (text) {
                    return (
                        <div className={`flex justify-center items-center ${bgColor} rounded mx-auto mt-0.5`} style={{ padding: '0.5px 2px', borderRadius: '4px' }}>
                            <span className='text-[6px] text-white font-bold leading-tight'>{text}</span>
                        </div>
                    );
                }
                return <div className={`${bgColor} h-2 w-5 rounded-full mx-auto mt-1`} />;
            }

            // Show holiday marker if it's a holiday (and no attendance data)
            if (isHoliday) {
                return (
                    <div className="flex justify-center items-center bg-red-400 rounded mx-auto mt-0.5" style={{ padding: '0.5px 2px', borderRadius: '4px' }}>
                        <span className='text-[6px] text-white font-bold leading-tight'>Libur</span>
                    </div>
                );
            }
        }
        return null;
    };

    const getStatusBadge = (item) => {
        if (item.status === 'izin') {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300 shadow-sm">
                    <FaFileAlt className="text-xs" />
                    Izin
                </span>
            );
        } else if (item.status === 'cuti') {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300 shadow-sm">
                    <FaFileAlt className="text-xs" />
                    Cuti
                </span>
            );
        } else if (item.waktuMasuk && item.waktuKeluar) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300 shadow-sm">
                    <FaCheckCircle className="text-xs" />
                    Hadir
                </span>
            );
        } else if (item.waktuMasuk) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300 shadow-sm">
                    <FaClock className="text-xs" />
                    Masuk
                </span>
            );
        } else {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300 shadow-sm">
                    <FaTimesCircle className="text-xs" />
                    Tidak Hadir
                </span>
            );
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (!reportData) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">Data tidak ditemukan</p>
            </div>
        );
    }

    const { user, statistics, attendanceData, period } = reportData;

    return (
        <div className="p-2 md:p-4 text-left">
            {/* Header */}
            <div className="mb-3 flex flex-row items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <button
                        onClick={() => navigate('/admin/users')}
                        className="p-1.5 text-zafu-muted hover:bg-zafu-cream rounded-lg shrink-0"
                    >
                        <FaArrowLeft />
                    </button>
                    <div className="min-w-0">
                        <h1 className="text-base md:text-lg font-semibold text-zafu-ink truncate">Rekap Absensi</h1>
                        <p className="text-xs text-zafu-muted truncate">{user.nama}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="px-2 py-1.5 border border-zafu-cream-dark rounded-lg text-sm bg-white h-9 w-[140px] sm:w-auto"
                    />
                    <button
                        onClick={handleExportExcel}
                        className="w-9 h-9 flex items-center justify-center bg-zafu-green text-white rounded-lg"
                        title="Excel"
                    >
                        <FaRegFileExcel />
                    </button>
                    <button
                        onClick={handlePrint}
                        className="w-9 h-9 flex items-center justify-center bg-zafu-green-soft text-white rounded-lg print:hidden"
                        title="Print"
                    >
                        <FaPrint />
                    </button>
                </div>
            </div>

            {/* User Info — compact */}
            <div className="bg-white rounded-xl border border-zafu-cream-dark/60 px-3 py-2 mb-3 flex items-center gap-3">
                <div className="shrink-0">
                    {user.foto_profile ? (
                        <AuthImage
                            kind="profile"
                            src={user.foto_profile}
                            alt={user.nama}
                            className="w-10 h-10 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-zafu-cream flex items-center justify-center">
                            <FaUser className="text-zafu-muted text-sm" />
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1 text-xs text-zafu-muted truncate">
                    <span className="font-semibold text-zafu-ink text-sm">{user.nama}</span>
                    <span className="mx-1">·</span>@{user.username}
                    <span className="mx-1">·</span>{user.devisi || '-'}
                    {user.lokasi ? <><span className="mx-1">·</span>{user.lokasi}</> : null}
                </div>
            </div>

            {/* Stats + target — compact row */}
            <div className="grid grid-cols-3 gap-1.5 mb-2">
                <div className="bg-white rounded-xl border border-zafu-cream-dark/60 px-2.5 py-2">
                    <p className="text-[10px] text-zafu-muted">Hadir</p>
                    <p className="text-lg font-bold text-green-600 leading-tight">{statistics.totalHadir}</p>
                </div>
                <div className="bg-white rounded-xl border border-zafu-cream-dark/60 px-2.5 py-2">
                    <p className="text-[10px] text-zafu-muted">Izin</p>
                    <p className="text-lg font-bold text-blue-600 leading-tight">{statistics.totalIzin}</p>
                </div>
                <div className="bg-white rounded-xl border border-zafu-cream-dark/60 px-2.5 py-2">
                    <p className="text-[10px] text-zafu-muted">Cuti</p>
                    <p className="text-lg font-bold text-purple-600 leading-tight">{statistics.totalCuti}</p>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-3">
                <div className="bg-white rounded-xl border border-zafu-cream-dark/60 px-2.5 py-2 flex items-center justify-between gap-2">
                    <div>
                        <p className="text-[10px] text-zafu-muted">Target / hari</p>
                        <p className="text-sm font-bold text-zafu-green">{formatNumber(user.standard_jam_kerja || 7)} jam</p>
                    </div>
                    <FaClock className="text-zafu-green/40" />
                </div>
                <div className="bg-white rounded-xl border border-zafu-cream-dark/60 px-2.5 py-2 flex items-center justify-between gap-2">
                    <div>
                        <p className="text-[10px] text-zafu-muted">Hari kerja</p>
                        <p className="text-sm font-bold text-zafu-green">{statistics.totalHariKerja} hari</p>
                    </div>
                    <FaCalendarAlt className="text-zafu-green/40" />
                </div>
            </div>

            {/* Attendance List */}
            <div ref={tableRef} className="mb-10 print-table-container">
                <div className="bg-zafu-green rounded-xl px-3 py-2.5 mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <FaFileAlt className="text-white/80 shrink-0" />
                        <div className="min-w-0">
                            <h2 className="text-sm font-semibold text-white">Detail Absensi</h2>
                            <p className="text-[11px] text-white/70 truncate">{period}</p>
                        </div>
                    </div>
                </div>

                {/* List Container */}
                <div className="p-0 mt-2 ">
                    {allDaysInMonth && allDaysInMonth.length > 0 ? (
                        <div className="space-y-0.5">
                            {allDaysInMonth.map((dayData, index) => {
                                const { tanggal, dateStr, dayOfWeek, isHoliday, holiday, attendance } = dayData;
                                const selectedDateStr = selectedDate ? formatDate01(selectedDate) : null;
                                const isSelected = selectedDateStr && dateStr === selectedDateStr;

                                // Determine keterangan based on Excel logic
                                let keterangan = '';
                                let jamMasuk = '';
                                let jamPulang = '';
                                let jumlahJamKerja = '';
                                let statusColor = 'gray';
                                let statusText = '';

                                if (attendance) {
                                    // Priority 1: Check status (izin/cuti)
                                    if (attendance.status === 'izin') {
                                        keterangan = 'Izin';
                                        statusColor = 'blue';
                                        statusText = 'Izin';
                                    } else if (attendance.status === 'cuti') {
                                        keterangan = 'Cuti';
                                        statusColor = 'purple';
                                        statusText = 'Cuti';
                                    }
                                    // Priority 2: Check if it's a holiday
                                    else if (isHoliday && holiday) {
                                        keterangan = holiday.nama;
                                        jamMasuk = attendance.waktuMasuk ? formatTime(attendance.waktuMasuk) : '';
                                        jamPulang = attendance.waktuKeluar ? formatTime(attendance.waktuKeluar) : '';
                                        statusColor = 'red';
                                        statusText = 'Libur';
                                    }
                                    // Priority 3: Check if has attendance data
                                    else if (attendance.waktuMasuk && attendance.waktuKeluar) {
                                        jamMasuk = formatTime(attendance.waktuMasuk);
                                        jamPulang = formatTime(attendance.waktuKeluar);
                                        jumlahJamKerja = attendance.jumlahJamKerja > 0 ? formatJamKerja(attendance.jumlahJamKerja) : '';
                                        keterangan = attendance.keterangan || '';
                                        statusColor = 'green';
                                        statusText = 'Hadir';
                                    } else if (attendance.waktuMasuk) {
                                        jamMasuk = formatTime(attendance.waktuMasuk);
                                        keterangan = 'Belum pulang';
                                        statusColor = 'yellow';
                                        statusText = 'Masuk';
                                    }
                                } else {
                                    // No attendance data - check if it's Sunday, holiday, or regular day
                                    if (dayOfWeek === 0) {
                                        keterangan = 'Minggu';
                                        statusColor = 'gray';
                                        statusText = 'Minggu';
                                    } else if (isHoliday && holiday) {
                                        keterangan = holiday.nama;
                                        statusColor = 'red';
                                        statusText = 'Libur';
                                    } else {
                                        keterangan = 'Absen';
                                        statusColor = 'red';
                                        statusText = 'Tidak Hadir';
                                    }
                                }

                                // Determine card styling
                                let cardClass = 'bg-white rounded-md hover:shadow-md transition-shadow cursor-pointer border-l-2';
                                let textClass = 'text-gray-900';

                                if (isSelected) {
                                    cardClass += ' border-yellow-500 bg-yellow-50';
                                } else if (dayOfWeek === 0) {
                                    cardClass = 'bg-transparent hover:bg-transparent rounded-md transition-colors cursor-default border-l-2 border-transparent';
                                    textClass = 'text-gray-400';
                                } else if (isHoliday) {
                                    cardClass += ' border-red-400 bg-red-50';
                                    textClass = 'text-red-800';
                                } else if (attendance && attendance.waktuMasuk && attendance.waktuKeluar) {
                                    cardClass += ' border-green-500';
                                } else if (attendance) {
                                    cardClass += ' border-yellow-400';
                                } else {
                                    cardClass += ' border-gray-300';
                                }

                                // Status color classes
                                const statusColorMap = {
                                    green: { bg: 'bg-green-100', text: 'text-green-700' },
                                    blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
                                    purple: { bg: 'bg-purple-100', text: 'text-purple-700' },
                                    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
                                    red: { bg: 'bg-red-100', text: 'text-red-700' },
                                    gray: { bg: 'bg-gray-100', text: 'text-gray-700' }
                                };
                                const statusStyle = statusColorMap[statusColor] || statusColorMap.gray;

                                return (
                                    <div
                                        key={index}
                                        className={`${cardClass} border border-gray-200`}
                                        onClick={() => {
                                            if (attendance) {
                                                setDetailModal({ isOpen: true, data: attendance });
                                            }
                                        }}
                                    >
                                        <div className="px-2 py-1">
                                            <div className="flex items-center gap-1.5">
                                                <div className={`shrink-0 w-8 h-8 rounded-md ${statusStyle.bg} flex flex-col items-center justify-center`}>
                                                    <span className={`text-xs font-bold ${statusStyle.text} leading-none`}>
                                                        {tanggal.getDate()}
                                                    </span>
                                                    <span className={`text-[8px] font-medium ${statusStyle.text} opacity-70 uppercase leading-none mt-0.5`}>
                                                        {formatDayName(tanggal).substring(0, 3)}
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0 flex items-center gap-1 flex-wrap">
                                                    {(jamMasuk || jamPulang || jumlahJamKerja) ? (
                                                        <>
                                                            {jamMasuk && (
                                                                <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-green-50 rounded text-[10px] font-semibold text-green-700">
                                                                    <FaSignInAlt className="text-[8px]" />{jamMasuk}
                                                                </span>
                                                            )}
                                                            {jamPulang && (
                                                                <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-blue-50 rounded text-[10px] font-semibold text-blue-700">
                                                                    <FaSignOutAlt className="text-[8px]" />{jamPulang}
                                                                </span>
                                                            )}
                                                            {jumlahJamKerja && (
                                                                <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-purple-50 rounded text-[10px] font-semibold text-purple-700">
                                                                    <FaClock className="text-[8px]" />{jumlahJamKerja}
                                                                </span>
                                                            )}
                                                            {keterangan && keterangan !== '-' && (
                                                                <span className="text-[10px] text-zafu-muted truncate">{keterangan}</span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="text-[10px] text-zafu-muted truncate">{keterangan || statusText}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${statusStyle.bg} ${statusStyle.text}`}>
                                                        {statusText}
                                                    </span>
                                                    {attendance?.fotoMasuk ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openImageModal(attendance.fotoMasuk, `Foto Masuk - ${formatDate(tanggal)}`, attendance, 'masuk');
                                                            }}
                                                            className="shrink-0"
                                                            title="Foto Masuk"
                                                        >
                                                            <AuthImage
                                                                src={attendance.fotoMasuk}
                                                                alt=""
                                                                className="w-7 h-7 object-cover rounded border border-gray-200"
                                                            />
                                                        </button>
                                                    ) : null}
                                                    {attendance?.fotoKeluar ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openImageModal(attendance.fotoKeluar, `Foto Keluar - ${formatDate(tanggal)}`, attendance, 'keluar');
                                                            }}
                                                            className="shrink-0"
                                                            title="Foto Keluar"
                                                        >
                                                            <AuthImage
                                                                src={attendance.fotoKeluar}
                                                                alt=""
                                                                className="w-7 h-7 object-cover rounded border border-gray-200"
                                                            />
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <div className="flex flex-col items-center justify-center text-gray-400">
                                <FaCalendarTimes className="text-4xl mb-3" />
                                <p className="text-lg font-medium">Tidak ada data absensi untuk periode ini</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            <Modal
                isOpen={detailModal.isOpen}
                onRequestClose={closeDetailModal}
                contentLabel="Detail Absensi"
                ariaHideApp={false}
                className="fixed inset-0 z-50 outline-none"
                overlayClassName="fixed inset-0 bg-black bg-opacity-75 z-40"
                style={{
                    content: {
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        padding: '0',
                        border: 'none',
                        background: 'transparent',
                        overflow: 'visible',
                        maxHeight: '90vh',
                        width: 'calc(100% - 2rem)',
                        maxWidth: '42rem'
                    }
                }}
            >
                {detailModal.data && (
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto relative">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h3 className="text-xl font-semibold text-gray-800">
                                Detail Absensi - {formatDate(detailModal.data.tanggal)}
                            </h3>
                            <button
                                onClick={closeDetailModal}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <FaTimes className="text-xl" />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Status */}
                            <div>
                                <label className="text-sm font-medium text-gray-500">Status</label>
                                <div className="mt-1">
                                    {getStatusBadge(detailModal.data)}
                                </div>
                            </div>

                            {/* Waktu Masuk */}
                            <div>
                                <label className="text-sm font-medium text-gray-500">Waktu Masuk</label>
                                <p className="mt-1 text-lg text-gray-900">
                                    {detailModal.data.waktuMasuk ? formatTime(detailModal.data.waktuMasuk) : '-'}
                                </p>
                                {detailModal.data.fotoMasuk && (
                                    <div className="mt-3">
                                        <button
                                            onClick={() => openImageModal(detailModal.data.fotoMasuk, `Foto Masuk - ${formatDate(detailModal.data.tanggal)}`, detailModal.data, 'masuk')}
                                            className="group relative w-full"
                                        >
                                            <div className="relative w-full max-w-md mx-auto">
                                                <AuthImage
                                                    src={detailModal.data.fotoMasuk}
                                                    alt="Foto Masuk"
                                                    className="w-full rounded-lg border-2 border-gray-300 group-hover:border-blue-500 transition-all cursor-pointer shadow-md"
                                                />
                                                {/* IP Address and Location Name overlay */}
                                                <div className="absolute top-2 left-2 right-2 flex justify-between items-start gap-2 z-10">
                                                    {detailModal.data.ipAddressMasuk && (
                                                        <div className="bg-black/30 backdrop-blur-sm text-white px-2 py-1 rounded-md text-xs md:text-sm flex items-center gap-1">
                                                            <FaNetworkWired className="text-xs" />
                                                            <span className="font-semibold">{detailModal.data.ipAddressMasuk}</span>
                                                        </div>
                                                    )}
                                                    {detailModal.data.locationNameMasuk && (
                                                        <div className="bg-black/30 backdrop-blur-sm text-white px-2 py-1 rounded-md text-xs md:text-sm ml-auto flex items-center gap-1">
                                                            <FaMapMarkerAlt className="text-xs" />
                                                            <span className="font-semibold">{detailModal.data.locationNameMasuk}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Time overlay di tengah */}
                                                {detailModal.data.waktuMasuk && (
                                                    <div className="absolute inset-0 flex items-center justify-center z-10">
                                                        <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg border border-white/20">
                                                            <div className="text-center">
                                                                <div className="text-xs md:text-sm text-white/80 mb-1">Waktu Absen Masuk</div>
                                                                <div className="text-lg md:text-xl font-bold">{formatTime(detailModal.data.waktuMasuk)}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Waktu Keluar */}
                            <div>
                                <label className="text-sm font-medium text-gray-500">Waktu Keluar</label>
                                <p className="mt-1 text-lg text-gray-900">
                                    {detailModal.data.waktuKeluar ? formatTime(detailModal.data.waktuKeluar) : '-'}
                                </p>
                                {detailModal.data.fotoKeluar && (
                                    <div className="mt-3">
                                        <button
                                            onClick={() => openImageModal(detailModal.data.fotoKeluar, `Foto Keluar - ${formatDate(detailModal.data.tanggal)}`, detailModal.data, 'keluar')}
                                            className="group relative w-full"
                                        >
                                            <div className="relative w-full max-w-md mx-auto">
                                                <AuthImage
                                                    src={detailModal.data.fotoKeluar}
                                                    alt="Foto Keluar"
                                                    className="w-full rounded-lg border-2 border-gray-300 group-hover:border-blue-500 transition-all cursor-pointer shadow-md"
                                                />
                                                {/* IP Address and Location Name overlay */}
                                                <div className="absolute top-2 left-2 right-2 flex justify-between items-start gap-2 z-10">
                                                    {detailModal.data.ipAddressKeluar && (
                                                        <div className="bg-black/30 backdrop-blur-sm text-white px-2 py-1 rounded-md text-xs md:text-sm flex items-center gap-1">
                                                            <FaNetworkWired className="text-xs" />
                                                            <span className="font-semibold">{detailModal.data.ipAddressKeluar}</span>
                                                        </div>
                                                    )}
                                                    {detailModal.data.locationNameKeluar && (
                                                        <div className="bg-black/30 backdrop-blur-sm text-white px-2 py-1 rounded-md text-xs md:text-sm ml-auto flex items-center gap-1">
                                                            <FaMapMarkerAlt className="text-xs" />
                                                            <span className="font-semibold">{detailModal.data.locationNameKeluar}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Time overlay di tengah */}
                                                {detailModal.data.waktuKeluar && (
                                                    <div className="absolute inset-0 flex items-center justify-center z-10">
                                                        <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg border border-white/20">
                                                            <div className="text-center">
                                                                <div className="text-xs md:text-sm text-white/80 mb-1">Waktu Absen Pulang</div>
                                                                <div className="text-lg md:text-xl font-bold">{formatTime(detailModal.data.waktuKeluar)}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Jam Kerja */}
                            {detailModal.data.jumlahJamKerja > 0 && (
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Jam Kerja</label>
                                    <p className="mt-1 text-lg text-gray-900">
                                        {formatJamKerja(detailModal.data.jumlahJamKerja)}
                                    </p>
                                </div>
                            )}

                            {/* Keterangan */}
                            {detailModal.data.keterangan && (
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Keterangan</label>
                                    <p className="mt-1 text-gray-900">{detailModal.data.keterangan}</p>
                                </div>
                            )}

                            {/* IP Address & Location Info Masuk */}
                            {(detailModal.data.ipAddressMasuk || detailModal.data.latitudeMasuk || detailModal.data.longitudeMasuk || detailModal.data.locationNameMasuk) && (
                                <div className="border-t pt-4">
                                    <label className="text-sm font-semibold text-gray-700 mb-3 block">📍 Informasi Lokasi Masuk</label>
                                    {detailModal.data.ipAddressMasuk && (
                                        <div className="mb-2">
                                            <label className="text-xs font-medium text-gray-500">IP Address</label>
                                            <p className="mt-1 text-gray-900 font-mono text-sm">{detailModal.data.ipAddressMasuk}</p>
                                        </div>
                                    )}
                                    {detailModal.data.locationNameMasuk && (
                                        <div className="mb-2">
                                            <label className="text-xs font-medium text-gray-500">Nama Lokasi</label>
                                            <p className="mt-1 text-gray-900">{detailModal.data.locationNameMasuk}</p>
                                        </div>
                                    )}
                                    {(detailModal.data.latitudeMasuk || detailModal.data.longitudeMasuk) && (
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Koordinat</label>
                                            <p className="mt-1 text-gray-600 text-sm font-mono">
                                                {detailModal.data.latitudeMasuk ? detailModal.data.latitudeMasuk.toFixed(6) : '-'}, {detailModal.data.longitudeMasuk ? detailModal.data.longitudeMasuk.toFixed(6) : '-'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* IP Address & Location Info Keluar */}
                            {(detailModal.data.ipAddressKeluar || detailModal.data.latitudeKeluar || detailModal.data.longitudeKeluar || detailModal.data.locationNameKeluar) && (
                                <div className="border-t pt-4">
                                    <label className="text-sm font-semibold text-gray-700 mb-3 block">📍 Informasi Lokasi Keluar</label>
                                    {detailModal.data.ipAddressKeluar && (
                                        <div className="mb-2">
                                            <label className="text-xs font-medium text-gray-500">IP Address</label>
                                            <p className="mt-1 text-gray-900 font-mono text-sm">{detailModal.data.ipAddressKeluar}</p>
                                        </div>
                                    )}
                                    {detailModal.data.locationNameKeluar && (
                                        <div className="mb-2">
                                            <label className="text-xs font-medium text-gray-500">Nama Lokasi</label>
                                            <p className="mt-1 text-gray-900">{detailModal.data.locationNameKeluar}</p>
                                        </div>
                                    )}
                                    {(detailModal.data.latitudeKeluar || detailModal.data.longitudeKeluar) && (
                                        <div>
                                            <label className="text-xs font-medium text-gray-500">Koordinat</label>
                                            <p className="mt-1 text-gray-600 text-sm font-mono">
                                                {detailModal.data.latitudeKeluar ? detailModal.data.latitudeKeluar.toFixed(6) : '-'}, {detailModal.data.longitudeKeluar ? detailModal.data.longitudeKeluar.toFixed(6) : '-'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Image Modal */}
            <Modal
                isOpen={imageModal.isOpen}
                onRequestClose={closeImageModal}
                contentLabel="Foto Absensi"
                ariaHideApp={false}
                className="fixed inset-0 flex items-center justify-center p-4 z-50"
                overlayClassName="fixed inset-0 bg-black bg-opacity-75 z-40"
            >
                <div className="bg-white rounded-lg shadow-xl max-w-screen max-h-screen w-full h-full flex flex-col relative overflow-hidden">
                    <div className="sticky top-0 bg-gradient-to-r from-[#1B3C33] via-[#7a3436] to-[#8b3d40] px-6 py-5 flex justify-between items-center flex-shrink-0 shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <FaImage className="text-white text-xl" />
                            </div>
                            <h3 className="text-xl font-bold text-white drop-shadow-md">{imageModal.title}</h3>
                        </div>
                        <button
                            onClick={closeImageModal}
                            className="p-2.5 text-white hover:text-gray-200 hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
                        >
                            <FaTimes className="text-xl" />
                        </button>
                    </div>
                    <div className="flex-1 p-6 overflow-auto flex items-center justify-center">
                        {imageModal.imageUrl && (
                            <div className="relative max-w-full max-h-full">
                                <AuthImage
                                    src={imageModal.imageUrl}
                                    alt={imageModal.title}
                                    className="max-w-full max-h-full w-auto h-auto object-cover rounded-lg shadow-md"
                                />
                                {/* IP Address and Location Name overlay */}
                                {imageModal.attendanceData && imageModal.type && (
                                    <>
                                        <div className="absolute top-4 left-4 right-4 flex justify-between items-start gap-2 z-10">
                                            {imageModal.type === 'masuk' && imageModal.attendanceData.ipAddressMasuk && (
                                                <div className="bg-black/30 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm md:text-base flex items-center gap-1.5">
                                                    <FaNetworkWired className="text-sm" />
                                                    <span className="font-semibold">{imageModal.attendanceData.ipAddressMasuk}</span>
                                                </div>
                                            )}
                                            {imageModal.type === 'keluar' && imageModal.attendanceData.ipAddressKeluar && (
                                                <div className="bg-black/30 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm md:text-base flex items-center gap-1.5">
                                                    <FaNetworkWired className="text-sm" />
                                                    <span className="font-semibold">{imageModal.attendanceData.ipAddressKeluar}</span>
                                                </div>
                                            )}
                                            {imageModal.type === 'masuk' && imageModal.attendanceData.locationNameMasuk && (
                                                <div className="bg-black/30 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm md:text-base ml-auto flex items-center gap-1.5">
                                                    <FaMapMarkerAlt className="text-sm" />
                                                    <span className="font-semibold">{imageModal.attendanceData.locationNameMasuk}</span>
                                                </div>
                                            )}
                                            {imageModal.type === 'keluar' && imageModal.attendanceData.locationNameKeluar && (
                                                <div className="bg-black/30 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm md:text-base ml-auto flex items-center gap-1.5">
                                                    <FaMapMarkerAlt className="text-sm" />
                                                    <span className="font-semibold">{imageModal.attendanceData.locationNameKeluar}</span>
                                                </div>
                                            )}
                                        </div>
                                        {/* Time overlay di tengah */}
                                        <div className="absolute inset-0 flex items-center justify-center z-10">
                                            {imageModal.type === 'masuk' && imageModal.attendanceData.waktuMasuk && (
                                                <div className="bg-black/60 backdrop-blur-sm text-white px-6 py-4 rounded-lg shadow-lg border border-white/20">
                                                    <div className="text-center">
                                                        <div className="text-sm md:text-base text-white/80 mb-1">Waktu Absen Masuk</div>
                                                        <div className="text-xl md:text-2xl font-bold">{formatTime(imageModal.attendanceData.waktuMasuk)}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {imageModal.type === 'keluar' && imageModal.attendanceData.waktuKeluar && (
                                                <div className="bg-black/60 backdrop-blur-sm text-white px-6 py-4 rounded-lg shadow-lg border border-white/20">
                                                    <div className="text-center">
                                                        <div className="text-sm md:text-base text-white/80 mb-1">Waktu Absen Pulang</div>
                                                        <div className="text-xl md:text-2xl font-bold">{formatTime(imageModal.attendanceData.waktuKeluar)}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ReportUser;

